import { ConfigApi, createApiRef } from '@backstage/core-plugin-api';
import { components, paths } from '@chanzuckerberg/agent-registry-api';
import { OktaTokenApi } from '@eng-portal/czi-extensions-react';
import createClient from 'openapi-fetch';

export type AgentProfile = components['schemas']['Profile'];
export type AgentProfileCreate = components['schemas']['ProfileCreate'];
export type AgentProfilePatch = components['schemas']['ProfilePatch'];
export type Agent = components['schemas']['Agent'];
export type AgentCreate = components['schemas']['AgentCreate'];
export type GrantStatus = components['schemas']['GrantStatus'];
export type ProviderGrant = components['schemas']['ProviderGrant'];

export type AgentProfilePatchFactory =
  | AgentProfilePatch
  | ((profile: AgentProfile) => AgentProfilePatch);

type VersionedAgent = {
  agent: Agent;
  etag: string;
};

type VersionedProfile = {
  profile: AgentProfile;
  etag: string;
};

export interface AgentRegistryAPI {
  listProfiles(): Promise<AgentProfile[]>;
  getProfile(name: string): Promise<AgentProfile>;
  createProfile(profile: AgentProfileCreate): Promise<AgentProfile>;
  updateProfile(
    name: string,
    patch: AgentProfilePatchFactory,
  ): Promise<AgentProfile>;
  deleteProfile(name: string): Promise<void>;
  listAgents(profileName: string): Promise<Agent[]>;
  getAgent(name: string): Promise<Agent>;
  createAgent(profileName: string, agent: AgentCreate): Promise<Agent>;
  suspendAgent(name: string): Promise<Agent>;
  resumeAgent(name: string): Promise<Agent>;
  deleteAgent(name: string): Promise<void>;
}

export const agentRegistryApiRef = createApiRef<AgentRegistryAPI>({
  id: 'agent-registry.api',
});

export class AgentRegistryClient implements AgentRegistryAPI {
  private readonly client: ReturnType<typeof createClient<paths>>;

  constructor(configApi: ConfigApi, oktaTokenApi: OktaTokenApi) {
    const config = configApi.getConfig('plugins.agentRegistry');
    const baseUrl = config.getString('baseUrl').replace(/\/$/, '');
    const clientId = config.getString('frontendOktaClientId');
    const issuerUrl = config.getString('frontendOktaIssuerUrl');
    this.client = createClient<paths>({
      baseUrl,
    });
    this.client.use({
      onRequest: async ({ request }) => {
        const token = await oktaTokenApi.getToken(clientId, issuerUrl, [
          'openid',
          'profile',
          'email',
          'groups',
        ]);
        request.headers.set('Authorization', `Bearer ${token}`);
        return request;
      },
    });
  }

  async listProfiles(): Promise<AgentProfile[]> {
    const profiles: AgentProfile[] = [];
    let continuation: string | undefined;
    do {
      const { data, error, response } = await this.client.GET('/profiles', {
        params: {
          query: {
            continue: continuation,
            limit: 200,
          },
        },
      });
      this.throwForError(error, response, 'list profiles');
      profiles.push(...(data?.items ?? []));
      continuation = data?.continue;
    } while (continuation);
    return profiles;
  }

  async createProfile(profile: AgentProfileCreate): Promise<AgentProfile> {
    const { data, error, response } = await this.client.POST('/profiles', {
      body: profile,
    });
    this.throwForError(error, response, 'create profile');
    if (!data) {
      throw new Error('create profile returned no profile');
    }
    return data;
  }

  async updateProfile(
    name: string,
    patch: AgentProfilePatchFactory,
  ): Promise<AgentProfile> {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const current = await this.getVersionedProfile(name);
      const resolvedPatch =
        typeof patch === 'function' ? patch(current.profile) : patch;
      const { data, error, response } = await this.client.PATCH(
        '/profiles/{name}',
        {
          body: resolvedPatch,
          params: {
            header: {
              'If-Match': current.etag,
            },
            path: {
              name,
            },
          },
        },
      );
      if (response.status === 412 && attempt === 0) {
        continue;
      }
      this.throwForError(error, response, 'update profile');
      if (!data) {
        throw new Error('update profile returned no profile');
      }
      return data;
    }
    throw new Error('profile remained stale after retry');
  }

  async deleteProfile(name: string): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const current = await this.getVersionedProfile(name);
      const { error, response } = await this.client.DELETE('/profiles/{name}', {
        params: {
          header: {
            'If-Match': current.etag,
          },
          path: {
            name,
          },
        },
      });
      if (response.status === 412 && attempt === 0) {
        continue;
      }
      this.throwForError(error, response, 'delete profile');
      return;
    }
  }

  async listAgents(profileName: string): Promise<Agent[]> {
    const agents: Agent[] = [];
    let continuation: string | undefined;
    do {
      const { data, error, response } = await this.client.GET(
        '/profiles/{name}/agents',
        {
          params: {
            path: {
              name: profileName,
            },
            query: {
              continue: continuation,
              limit: 200,
            },
          },
        },
      );
      this.throwForError(error, response, 'list agents');
      agents.push(...(data?.items ?? []));
      continuation = data?.continue;
    } while (continuation);
    return agents;
  }

  async getProfile(name: string): Promise<AgentProfile> {
    return (await this.getVersionedProfile(name)).profile;
  }

  async getAgent(name: string): Promise<Agent> {
    return (await this.getVersionedAgent(name)).agent;
  }

  async createAgent(profileName: string, agent: AgentCreate): Promise<Agent> {
    const { data, error, response } = await this.client.POST(
      '/profiles/{name}/agents',
      {
        body: agent,
        params: {
          path: {
            name: profileName,
          },
        },
      },
    );
    this.throwForError(error, response, 'create agent');
    if (!data) {
      throw new Error('create agent returned no agent');
    }
    return data;
  }

  async suspendAgent(name: string): Promise<Agent> {
    return this.changeAgentState(name, 'suspend');
  }

  async resumeAgent(name: string): Promise<Agent> {
    return this.changeAgentState(name, 'resume');
  }

  async deleteAgent(name: string): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const current = await this.getVersionedAgent(name);
      const { error, response } = await this.client.DELETE('/agents/{name}', {
        params: {
          header: {
            'If-Match': current.etag,
          },
          path: {
            name,
          },
        },
      });
      if (response.status === 412 && attempt === 0) {
        continue;
      }
      this.throwForError(error, response, 'delete agent');
      return;
    }
  }

  private async changeAgentState(
    name: string,
    action: 'suspend' | 'resume',
  ): Promise<Agent> {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const current = await this.getVersionedAgent(name);
      const options = {
        params: {
          header: {
            'If-Match': current.etag,
          },
          path: {
            name,
          },
        },
      };
      const result =
        action === 'suspend'
          ? await this.client.POST('/agents/{name}:suspend', options)
          : await this.client.POST('/agents/{name}:resume', options);
      if (result.response.status === 412 && attempt === 0) {
        continue;
      }
      this.throwForError(result.error, result.response, `${action} agent`);
      if (!result.data) {
        throw new Error(`${action} agent returned no agent`);
      }
      return result.data;
    }
    throw new Error(`${action} agent remained stale after retry`);
  }

  private async getVersionedAgent(name: string): Promise<VersionedAgent> {
    const { data, error, response } = await this.client.GET('/agents/{name}', {
      params: {
        path: {
          name,
        },
      },
    });
    this.throwForError(error, response, 'get agent');
    if (!data) {
      throw new Error('get agent returned no agent');
    }
    const etag = response.headers.get('ETag');
    if (!etag) {
      throw new Error('get agent returned no ETag');
    }
    return {
      agent: data,
      etag,
    };
  }

  private async getVersionedProfile(name: string): Promise<VersionedProfile> {
    const { data, error, response } = await this.client.GET(
      '/profiles/{name}',
      {
        params: {
          path: {
            name,
          },
        },
      },
    );
    this.throwForError(error, response, 'get profile');
    if (!data) {
      throw new Error('get profile returned no profile');
    }
    const etag = response.headers.get('ETag');
    if (!etag) {
      throw new Error('get profile returned no ETag');
    }
    return {
      profile: data,
      etag,
    };
  }

  private throwForError(
    error: components['schemas']['Error'] | undefined,
    response: Response,
    operation: string,
  ): void {
    if (response.ok) {
      return;
    }
    if (!error) {
      throw new Error(
        `${operation}: HTTP ${response.status} ${response.statusText}`,
      );
    }
    throw new Error(
      `${operation}: ${error.message} (${error.code}, request ${error.requestId}, HTTP ${response.status})`,
    );
  }
}
